import React, { useState, useCallback, useRef } from 'react';
import SMSLayout from '../components/SMSLayout';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload,
  FileSpreadsheet,
  Eye,
  Filter,
  Trash2,
  Download,
  Save,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Phone,
  Users,
  X,
  Search,
  MoreVertical,
  Database,
  Sparkles,
  ArrowUpDown,
  FileDown,
  Smartphone,
  Ban,
  Send
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Steps
const STEPS = {
  IMPORT: 1,
  PREVIEW: 2,
  CLEAN: 3,
  RESULT: 4
};

const DataImport = ({ useMainLayout = false }) => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef(null);
  
  // Wrapper component based on layout type
  const LayoutWrapper = useMainLayout ? Layout : SMSLayout;
  
  // Current step
  const [currentStep, setCurrentStep] = useState(STEPS.IMPORT);
  
  // Step 1: Import
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Step 2: Preview
  const [fileData, setFileData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [phoneColumn, setPhoneColumn] = useState('');
  
  // Step 3: Cleaning options
  const [cleaningOptions, setCleaningOptions] = useState({
    mobileOnly: true,
    deduplicate: true,
    removeEmpty: false,
    normalizeNumbers: false,
    excludeBlacklist: false
  });
  const [filters, setFilters] = useState([]);
  const [cleaning, setCleaning] = useState(false);
  const [cleaningProgress, setCleaningProgress] = useState(0);
  
  // Step 4: Result
  const [cleanedData, setCleanedData] = useState(null);
  const [cleaningStats, setCleaningStats] = useState(null);
  const [resultPreview, setResultPreview] = useState([]);
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Saved files list
  const [savedFiles, setSavedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // View mode: 'import' or 'files'
  const [viewMode, setViewMode] = useState('import');
  
  // For centralized mode: target selection (SMS or RCS)
  const [useFileTarget, setUseFileTarget] = useState(null);

  // Sanitize filename
  const sanitizeFileName = (name) => {
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 50);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(ext)) {
      toast.error('Format non supporté. Utilisez CSV, XLS ou XLSX.');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('preview_rows', '25');
      
      const response = await axios.post(`${API_URL}/data-import/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      const baseName = file.name.replace(/\.(csv|xlsx|xls)$/i, '');
      setFileName(sanitizeFileName(baseName) + '_nettoye_' + new Date().toISOString().split('T')[0]);
      
      setUploadedFile({ name: file.name, size: file.size });
      setFileData(response.data.file_id);
      setColumns(response.data.columns || []);
      setPreviewRows(response.data.preview || []);
      setTotalRows(response.data.total_rows || 0);
      
      // Auto-detect phone column
      const phoneColNames = ['mobile', 'tel', 'telephone', 'phone', 'numero', 'numéro', 'gsm', 'portable', 'n°', 'num'];
      const detectedCol = response.data.columns?.find(col => 
        phoneColNames.some(name => col.toLowerCase().includes(name))
      );
      if (detectedCol) setPhoneColumn(detectedCol);
      
      setCurrentStep(STEPS.PREVIEW);
      toast.success(`${response.data.total_rows?.toLocaleString()} lignes chargées`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Add filter
  const addFilter = () => {
    setFilters([...filters, { column: columns[0] || '', condition: 'contains', value: '' }]);
  };

  // Remove filter
  const removeFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  // Update filter
  const updateFilter = (index, field, value) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [field]: value };
    setFilters(newFilters);
  };

  // Apply cleaning
  const handleClean = async () => {
    if (!phoneColumn) {
      toast.error('Sélectionnez la colonne téléphone');
      return;
    }
    
    setCleaning(true);
    setCleaningProgress(0);
    
    try {
      // Simulate progress for UX
      const progressInterval = setInterval(() => {
        setCleaningProgress(prev => Math.min(prev + 10, 90));
      }, 500);
      
      const response = await axios.post(`${API_URL}/data-import/clean`, {
        file_id: fileData,
        phone_column: phoneColumn,
        options: cleaningOptions,
        filters: filters.filter(f => f.value.trim())
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      clearInterval(progressInterval);
      setCleaningProgress(100);
      
      setCleanedData(response.data.cleaned_file_id);
      setCleaningStats(response.data.stats);
      setResultPreview(response.data.preview || []);
      
      setCurrentStep(STEPS.RESULT);
      toast.success('Nettoyage terminé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du nettoyage');
    } finally {
      setCleaning(false);
      setCleaningProgress(0);
    }
  };

  // Save cleaned file
  const handleSave = async (format = 'internal') => {
    if (!fileName.trim()) {
      toast.error('Entrez un nom de fichier');
      return;
    }
    
    setSaving(true);
    try {
      if (format === 'internal') {
        await axios.post(`${API_URL}/data-import/save`, {
          file_id: cleanedData,
          name: fileName,
          phone_column: phoneColumn,
          stats: cleaningStats
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Fichier sauvegardé pour utilisation SMS');
        loadSavedFiles();
        setViewMode('files');
      } else {
        // Export CSV or XLSX
        const response = await axios.get(`${API_URL}/data-import/export/${cleanedData}`, {
          params: { format },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        toast.success(`Fichier exporté en ${format.toUpperCase()}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Load saved files
  const loadSavedFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await axios.get(`${API_URL}/data-import/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedFiles(response.data || []);
    } catch (error) {
      console.log('Error loading files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Delete saved file
  const handleDeleteFile = async () => {
    if (!deleteTarget) return;
    
    try {
      await axios.delete(`${API_URL}/data-import/files/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fichier supprimé');
      setDeleteTarget(null);
      loadSavedFiles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Use file for SMS campaign
  const handleUseFile = (file, targetModule = null) => {
    if (useMainLayout && !targetModule) {
      // In central mode, show selection dialog
      setUseFileTarget(file);
    } else {
      const target = targetModule || 'sms';
      if (target === 'sms') {
        window.location.href = `/sms/mailing?file_id=${file.id}`;
      } else if (target === 'rcs') {
        window.location.href = `/rcs-campaign?file_id=${file.id}`;
      }
    }
  };
  
  // Confirm use file and navigate
  const confirmUseFile = (target) => {
    if (useFileTarget) {
      handleUseFile(useFileTarget, target);
      setUseFileTarget(null);
    }
  };

  // Reset to start
  const handleReset = () => {
    setCurrentStep(STEPS.IMPORT);
    setUploadedFile(null);
    setFileData(null);
    setColumns([]);
    setPreviewRows([]);
    setTotalRows(0);
    setPhoneColumn('');
    setCleaningOptions({
      mobileOnly: true,
      deduplicate: true,
      removeEmpty: false,
      normalizeNumbers: false,
      excludeBlacklist: false
    });
    setFilters([]);
    setCleanedData(null);
    setCleaningStats(null);
    setResultPreview([]);
    setFileName('');
  };

  // Load files on mount
  React.useEffect(() => {
    loadSavedFiles();
  }, []);

  // Filter saved files
  const filteredFiles = savedFiles.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render step indicator - memoized to avoid re-creation
  const stepIndicatorContent = (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[
        { step: STEPS.IMPORT, label: 'Import', icon: Upload },
        { step: STEPS.PREVIEW, label: 'Aperçu', icon: Eye },
        { step: STEPS.CLEAN, label: 'Nettoyage', icon: Sparkles },
        { step: STEPS.RESULT, label: 'Résultat', icon: CheckCircle }
      ].map(({ step, label, icon: Icon }, index) => (
        <React.Fragment key={step}>
          <div 
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
              currentStep === step
                ? 'bg-indigo-600 text-white'
                : currentStep > step
                  ? isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                  : isDarkMode ? 'bg-[hsl(220,14%,18%)] text-slate-500' : 'bg-slate-100 text-slate-400'
            }`}
          >
            {currentStep > step ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <Icon className="h-4 w-4" />
            )}
            <span className="text-sm font-medium hidden sm:inline">{label}</span>
          </div>
          {index < 3 && (
            <ChevronRight className={`h-4 w-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <LayoutWrapper>
      <div className="space-y-6" data-testid="data-import-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {useMainLayout ? 'Gestion des données' : 'Import de données'}
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {useMainLayout 
                ? 'Centralisez, importez et nettoyez vos listes de contacts pour SMS et RCS'
                : 'Importez, nettoyez et sauvegardez vos listes de contacts'
              }
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant={viewMode === 'import' ? 'default' : 'outline'}
              onClick={() => { setViewMode('import'); handleReset(); }}
              className={viewMode === 'import' ? 'bg-indigo-600' : ''}
            >
              <Upload className="h-4 w-4 mr-2" />
              Importer
            </Button>
            <Button 
              variant={viewMode === 'files' ? 'default' : 'outline'}
              onClick={() => { setViewMode('files'); loadSavedFiles(); }}
              className={viewMode === 'files' ? 'bg-indigo-600' : ''}
            >
              <Database className="h-4 w-4 mr-2" />
              Mes fichiers
              {savedFiles.length > 0 && (
                <Badge variant="secondary" className="ml-2">{savedFiles.length}</Badge>
              )}
            </Button>
          </div>
        </div>

        {viewMode === 'import' ? (
          <>
            {/* Step Indicator */}
            {stepIndicatorContent}

            {/* Step 1: Import */}
            {currentStep === STEPS.IMPORT && (
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                    <Upload className="h-5 w-5" />
                    Importer un fichier
                  </CardTitle>
                  <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Formats supportés : CSV, XLS, XLSX (max. 150 000 lignes)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all hover:border-indigo-500 ${
                      isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,16%)]' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {uploading ? (
                      <div className="space-y-4">
                        <Loader2 className={`h-12 w-12 mx-auto animate-spin ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                        <p className={isDarkMode ? 'text-white' : 'text-slate-700'}>
                          Chargement en cours...
                        </p>
                        <div className="max-w-md mx-auto space-y-2">
                          <Progress value={uploadProgress} className="h-3" />
                          <div className="flex items-center justify-between text-sm">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                              Progression : {uploadProgress}%
                            </span>
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                              {uploadProgress < 100 ? 'Analyse du fichier...' : 'Finalisation...'}
                            </span>
                          </div>
                        </div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          Les fichiers volumineux peuvent prendre plusieurs minutes
                        </p>
                      </div>
                    ) : (
                      <>
                        <FileSpreadsheet className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                        <p className={`text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                          Glissez votre fichier ici
                        </p>
                        <p className={`mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          ou cliquez pour sélectionner (max. 150 000 lignes)
                        </p>
                        <Button>
                          <Upload className="h-4 w-4 mr-2" />
                          Sélectionner un fichier
                        </Button>
                      </>
                    )}
                  </div>
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    accept=".csv,.xls,.xlsx" 
                    onChange={handleFileUpload} 
                    className="hidden" 
                  />
                </CardContent>
              </Card>
            )}

            {/* Step 2: Preview */}
            {currentStep === STEPS.PREVIEW && (
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                        <Eye className="h-5 w-5" />
                        Aperçu des données
                      </CardTitle>
                      <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                        {uploadedFile?.name} • {totalRows.toLocaleString()} lignes • {columns.length} colonnes
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleReset}>
                      <X className="h-4 w-4 mr-2" />
                      Annuler
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Preview table */}
                  <div className="overflow-x-auto rounded-lg border max-h-96">
                    <table className={`w-full text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      <thead className={`sticky top-0 ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-100'}`}>
                        <tr>
                          <th className="px-3 py-2 text-left font-medium w-12">#</th>
                          {columns.map((col) => (
                            <th key={col} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? '' : isDarkMode ? 'bg-[hsl(220,14%,16%)]' : 'bg-slate-50'}>
                            <td className={`px-3 py-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{idx + 1}</td>
                            {columns.map((col) => (
                              <td key={col} className="px-3 py-2 whitespace-nowrap">{row[col]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {previewRows.length < totalRows && (
                    <p className={`text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Affichage des 25 premières lignes sur {totalRows.toLocaleString()}
                    </p>
                  )}
                  
                  {/* Phone column selection */}
                  <div className="flex items-end gap-4">
                    <div className="flex-1 space-y-2">
                      <Label className={isDarkMode ? 'text-slate-200' : ''}>
                        <Phone className="h-4 w-4 inline mr-2" />
                        Colonne contenant les numéros de téléphone *
                      </Label>
                      <Select value={phoneColumn} onValueChange={setPhoneColumn}>
                        <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                          <SelectValue placeholder="Sélectionnez la colonne" />
                        </SelectTrigger>
                        <SelectContent>
                          {columns.map((col) => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button 
                      onClick={() => setCurrentStep(STEPS.CLEAN)}
                      disabled={!phoneColumn}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      Continuer
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Cleaning */}
            {currentStep === STEPS.CLEAN && (
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                        <Sparkles className="h-5 w-5" />
                        Nettoyage et filtrage
                      </CardTitle>
                      <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                        Configurez les options de nettoyage pour vos {totalRows.toLocaleString()} lignes
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setCurrentStep(STEPS.PREVIEW)}>
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Retour
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Cleaning options */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                      <h3 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <Smartphone className="h-4 w-4" />
                        Filtrage des numéros
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="mobileOnly" 
                            checked={cleaningOptions.mobileOnly}
                            onCheckedChange={(checked) => setCleaningOptions({...cleaningOptions, mobileOnly: checked})}
                          />
                          <Label htmlFor="mobileOnly" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Mobiles uniquement (06, 07, +33 6/7, +262 692/693)
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="deduplicate" 
                            checked={cleaningOptions.deduplicate}
                            onCheckedChange={(checked) => setCleaningOptions({...cleaningOptions, deduplicate: checked})}
                          />
                          <Label htmlFor="deduplicate" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Dédoublonnage (supprimer les numéros en double)
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="normalizeNumbers" 
                            checked={cleaningOptions.normalizeNumbers}
                            onCheckedChange={(checked) => setCleaningOptions({...cleaningOptions, normalizeNumbers: checked})}
                          />
                          <Label htmlFor="normalizeNumbers" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Normaliser au format international (+33...)
                          </Label>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                      <h3 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <Trash2 className="h-4 w-4" />
                        Nettoyage des données
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="removeEmpty" 
                            checked={cleaningOptions.removeEmpty}
                            onCheckedChange={(checked) => setCleaningOptions({...cleaningOptions, removeEmpty: checked})}
                          />
                          <Label htmlFor="removeEmpty" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Supprimer les lignes vides
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="excludeBlacklist" 
                            checked={cleaningOptions.excludeBlacklist}
                            onCheckedChange={(checked) => setCleaningOptions({...cleaningOptions, excludeBlacklist: checked})}
                          />
                          <Label htmlFor="excludeBlacklist" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Exclure les numéros blacklistés
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Advanced filters */}
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`font-medium flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <Filter className="h-4 w-4" />
                        Filtres avancés
                      </h3>
                      <Button variant="outline" size="sm" onClick={addFilter}>
                        + Ajouter un filtre
                      </Button>
                    </div>
                    
                    {filters.length === 0 ? (
                      <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Aucun filtre configuré
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {filters.map((filter, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Select value={filter.column} onValueChange={(v) => updateFilter(index, 'column', v)}>
                              <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {columns.map((col) => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            <Select value={filter.condition} onValueChange={(v) => updateFilter(index, 'condition', v)}>
                              <SelectTrigger className={`w-32 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="contains">Contient</SelectItem>
                                <SelectItem value="not_contains">Ne contient pas</SelectItem>
                                <SelectItem value="equals">Égal à</SelectItem>
                                <SelectItem value="not_equals">Différent de</SelectItem>
                                <SelectItem value="starts_with">Commence par</SelectItem>
                                <SelectItem value="ends_with">Termine par</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            <Input
                              value={filter.value}
                              onChange={(e) => updateFilter(index, 'value', e.target.value)}
                              placeholder="Valeur"
                              className={`flex-1 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                            />
                            
                            <Button variant="ghost" size="sm" onClick={() => removeFilter(index)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Apply button */}
                  <div className="flex justify-end">
                    <Button 
                      onClick={handleClean}
                      disabled={cleaning}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      {cleaning ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Nettoyage en cours... {cleaningProgress}%
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Appliquer le nettoyage
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {cleaning && (
                    <Progress value={cleaningProgress} className="h-2" />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 4: Result */}
            {currentStep === STEPS.RESULT && cleaningStats && (
              <div className="space-y-6">
                {/* Stats */}
                <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      Nettoyage terminé
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'}`}>
                        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {cleaningStats.initial?.toLocaleString()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Lignes initiales</p>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                        <p className="text-2xl font-bold text-emerald-600">
                          {cleaningStats.valid_mobiles?.toLocaleString()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Mobiles valides</p>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                        <p className="text-2xl font-bold text-amber-600">
                          {cleaningStats.duplicates_removed?.toLocaleString()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Doublons supprimés</p>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'}`}>
                        <p className="text-2xl font-bold text-red-600">
                          {cleaningStats.invalid_removed?.toLocaleString()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Invalides exclus</p>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}>
                        <p className="text-2xl font-bold text-indigo-600">
                          {cleaningStats.final?.toLocaleString()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Lignes finales</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Preview result */}
                <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                      <Eye className="h-5 w-5" />
                      Aperçu du résultat (25 premières lignes)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto rounded-lg border max-h-64">
                      <table className={`w-full text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        <thead className={`sticky top-0 ${isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-100'}`}>
                          <tr>
                            <th className="px-3 py-2 text-left font-medium w-12">#</th>
                            {columns.map((col) => (
                              <th key={col} className="px-3 py-2 text-left font-medium whitespace-nowrap">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {resultPreview.map((row, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? '' : isDarkMode ? 'bg-[hsl(220,14%,16%)]' : 'bg-slate-50'}>
                              <td className={`px-3 py-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{idx + 1}</td>
                              {columns.map((col) => (
                                <td key={col} className="px-3 py-2 whitespace-nowrap">{row[col]}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Save */}
                <Card className={`border-2 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-indigo-500/30' : 'border-indigo-200'}`}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                      <Save className="h-5 w-5" />
                      Sauvegarder
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className={isDarkMode ? 'text-slate-200' : ''}>Nom du fichier</Label>
                      <Input
                        value={fileName}
                        onChange={(e) => setFileName(sanitizeFileName(e.target.value))}
                        placeholder="contacts_nettoyes_2026-01-22"
                        className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                      />
                    </div>
                    
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={() => handleSave('internal')}
                        disabled={saving || !fileName.trim()}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Sauvegarder pour SMS
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleSave('csv')}
                        disabled={saving}
                      >
                        <FileDown className="h-4 w-4 mr-2" />
                        Exporter CSV
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleSave('xlsx')}
                        disabled={saving}
                      >
                        <FileDown className="h-4 w-4 mr-2" />
                        Exporter XLSX
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={handleReset}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Nouveau fichier
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        ) : (
          /* Files list view */
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                  <Database className="h-5 w-5" />
                  Mes fichiers de données
                </CardTitle>
                <Button onClick={loadSavedFiles} variant="outline" size="sm">
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingFiles ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
              <div className="relative mt-2">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un fichier..."
                  className={`pl-10 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loadingFiles ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                </div>
              ) : filteredFiles.length === 0 ? (
                <div className="text-center py-12">
                  <Database className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {searchQuery ? 'Aucun fichier trouvé' : 'Aucun fichier sauvegardé'}
                  </h3>
                  <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                    {searchQuery ? 'Essayez une autre recherche' : 'Importez et nettoyez un fichier pour commencer'}
                  </p>
                  {!searchQuery && (
                    <Button className="mt-4" onClick={() => setViewMode('import')}>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer un fichier
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFiles.map((file) => (
                    <div 
                      key={file.id}
                      className={`p-4 rounded-lg flex items-center justify-between ${
                        isDarkMode ? 'bg-[hsl(220,14%,18%)] hover:bg-[hsl(220,14%,20%)]' : 'bg-slate-50 hover:bg-slate-100'
                      } transition-colors`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                          <FileSpreadsheet className={`h-6 w-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {file.name}
                          </p>
                          <div className={`flex items-center gap-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {file.contacts_count?.toLocaleString()} contacts
                            </span>
                            <span>•</span>
                            <span>
                              {new Date(file.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleUseFile(file)}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          Utiliser
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleUseFile(file)}>
                              <Send className="h-4 w-4 mr-2" />
                              Utiliser pour SMS
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteTarget(file)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <AlertDialogHeader>
              <AlertDialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Supprimer le fichier
              </AlertDialogTitle>
              <AlertDialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                Êtes-vous sûr de vouloir supprimer "{deleteTarget?.name}" ?
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white hover:bg-[hsl(220,14%,22%)]' : ''}>
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteFile} className="bg-red-600 hover:bg-red-700">
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Use file target selection (central mode only) */}
        {useMainLayout && (
          <AlertDialog open={!!useFileTarget} onOpenChange={() => setUseFileTarget(null)}>
            <AlertDialogContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <AlertDialogHeader>
                <AlertDialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                  <Send className="h-5 w-5 text-indigo-500" />
                  Utiliser ce fichier
                </AlertDialogTitle>
                <AlertDialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                  Dans quel module souhaitez-vous utiliser "{useFileTarget?.name}" ?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="grid grid-cols-2 gap-4 py-4">
                <Button
                  onClick={() => confirmUseFile('sms')}
                  className="h-20 flex flex-col items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="use-for-sms"
                >
                  <Smartphone className="h-6 w-6" />
                  <span>SMS Mailing</span>
                </Button>
                <Button
                  onClick={() => confirmUseFile('rcs')}
                  className="h-20 flex flex-col items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="use-for-rcs"
                >
                  <Send className="h-6 w-6" />
                  <span>RCS Campaign</span>
                </Button>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white hover:bg-[hsl(220,14%,22%)]' : ''}>
                  Annuler
                </AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </LayoutWrapper>
  );
};

// Export central version as named export
export const DataImportCentral = () => <DataImport useMainLayout={true} />;

export default DataImport;
